import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, Download, Paperclip, Filter } from "lucide-react";
import { api, API } from "../../lib/api";

export const STATUS_OPTIONS = [
  "Neu", "Kontaktaufnahme offen", "Kunde nicht erreicht", "Rückruf vereinbart",
  "Besichtigung vereinbart", "Besichtigung durchgeführt", "Angebot wird erstellt",
  "Angebot versendet", "Nachfassen", "Beauftragt", "Auftrag geplant",
  "Auftrag durchgeführt", "Abgeschlossen", "Abgelehnt", "Storniert", "Archiviert",
];

const statusColor = (s) => {
  if (s === "Neu") return "bg-yellow text-brand-black";
  if (["Abgeschlossen"].includes(s)) return "bg-green-600 text-white";
  if (["Abgelehnt", "Storniert", "Archiviert"].includes(s)) return "bg-red-500 text-white";
  return "bg-dark-gray text-white";
};

export default function Inquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [archived, setArchived] = useState(false);
  const [hasFiles, setHasFiles] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (archived) params.archived = true;
      if (hasFiles) params.has_files = true;
      const { data } = await api.get("/admin/inquiries", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [q, status, archived, hasFiles]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const exportCsv = () => {
    window.open(`${API}/admin/export/csv`, "_blank");
  };

  return (
    <div data-testid="admin-inquiries">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-anthracite">Anfragen</h1>
          <p className="mt-1 text-mid-gray">{items.length} Vorgänge</p>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-sm border border-anthracite px-4 py-2.5 font-heading text-sm font-bold text-anthracite transition-colors hover:bg-light-gray" data-testid="export-csv">
          <Download className="h-4 w-4" /> CSV-Export
        </button>
      </div>

      <div className="mt-6 flex flex-col gap-3 rounded-md border border-light-gray bg-white p-4 md:flex-row md:flex-wrap md:items-center">
        <div className="relative flex-1 md:min-w-[240px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mid-gray" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, Telefon, E-Mail, Nummer, Ort …" className="w-full rounded-sm border border-light-gray bg-light-gray py-2.5 pl-10 pr-3 text-sm outline-none focus:border-yellow" data-testid="inq-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-sm border border-light-gray bg-light-gray px-3 py-2.5 text-sm outline-none focus:border-yellow" data-testid="inq-status-filter">
          <option value="">Alle Status</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-anthracite"><input type="checkbox" checked={hasFiles} onChange={(e) => setHasFiles(e.target.checked)} className="accent-yellow" data-testid="inq-hasfiles" /> mit Dateien</label>
        <label className="flex items-center gap-2 text-sm text-anthracite"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} className="accent-yellow" data-testid="inq-archived" /> Archiv</label>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-yellow" /></div>
      ) : items.length === 0 ? (
        <p className="mt-10 rounded-md border border-dashed border-mid-gray/30 bg-white p-12 text-center text-mid-gray" data-testid="inq-empty">
          <Filter className="mx-auto mb-3 h-8 w-8 text-mid-gray/50" />
          Keine Anfragen gefunden.
        </p>
      ) : (
        <div className="mt-6 space-y-3">
          {items.map((i) => (
            <Link key={i.id} to={`/admin/anfragen/${i.id}`} className="flex flex-col gap-2 rounded-md border border-light-gray bg-white p-4 transition-colors hover:border-yellow md:flex-row md:items-center md:justify-between" data-testid={`inq-row-${i.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-heading font-bold text-anthracite">{i.name}</span>
                  {i.company && <span className="text-sm text-mid-gray">· {i.company}</span>}
                  {i.files?.length > 0 && <Paperclip className="h-4 w-4 text-mid-gray" />}
                </div>
                <p className="mt-0.5 text-sm text-mid-gray">{i.number} · {i.service} · {i.location} {i.postal_code ? `(${i.postal_code})` : ""}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-mid-gray">{new Date(i.created_at).toLocaleDateString("de-DE")}</span>
                <span className={`rounded-sm px-2.5 py-1 text-xs font-bold ${statusColor(i.status)}`}>{i.status}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
